import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Overlay } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import Icon from '../../../../components/icon/Icon';
import Badge from '../../badge/Badge';
import { Color } from '../../../../core/enums/Color';
import { Size } from '../../../../core/enums/Size';
import HeaderNotificationItem from './HeaderNotificationItem';
import { NOTIFICATIONS } from '../../../../core/constants/routes';
import NotificationsService from '../../../../core/services/NotificationsService';
import { Notification } from '../../../../core/state/notifications/types';
import { useInternalUser } from '../../../../core/hooks/useInternalUser';
import { sortNotificationsByDate } from './helpers';

import './HeaderNotification.scss';

const HeaderNotification: React.FC = () => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [notifications, setNotifications] = useState<Notification[] | null>(null);
  const target = useRef(null);
  const isInternalUser = useInternalUser();

  const getNotifications = async () => {
    const response = await NotificationsService.getNotifications(false);
    setNotifications(sortNotificationsByDate(response.data.content));
  };

  const markAsRead = async () => {
    const notificationsIds = notifications?.reduce((prev, curr, currentIndex) => {
      if (currentIndex === 0) {
        return `${curr.id}`;
      }
      return `${prev},${curr.id}`;
    }, '');

    if (notificationsIds) {
      await NotificationsService.markNotificationsAsRead(notificationsIds);
      getNotifications();
    }
  };

  useEffect(() => {
    getNotifications();
  }, []);

  return (
    <div className="header-notification">
      <button
        className="header-notification__icon"
        ref={target}
        onClick={() => {
          if (!show) {
            getNotifications();
          }
          setShow(!show);
        }}
      >
        {notifications?.length !== 0 && <span className="header-notification__icon-dot" />}
        <Icon icon="notification" size="small" svg />
      </button>
      <Overlay
        target={target.current}
        show={show}
        placement="bottom-end"
        onHide={() => {
          markAsRead();
          setShow(false);
        }}
        rootClose
      >
        {/* eslint-disable-next-line @typescript-eslint/no-unused-vars */}
        {({ placement, arrowProps, show: _show, popper, ...props }) => (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <div className="header-notification__overlay" {...props}>
            <div className="header-notification__inner">
              <header className="header-notification__header">
                <div className="header-notification__header-text">{t('notification.title')}</div>
                <Badge color={Color.JokerBlue} size={Size.Large} label={String(notifications?.length)} />
              </header>
              <section className="header-notification__content">
                {notifications?.length !== 0 ? (
                  notifications?.map(({ id, projectName, createdOn, createdBy, photo, notificationTypeID }) => (
                    <HeaderNotificationItem
                      image={photo}
                      key={id}
                      notificationType={notificationTypeID}
                      projectName={projectName}
                      date={createdOn}
                      name={createdBy}
                    />
                  ))
                ) : (
                  <div className="header-notification__empty">
                    <Icon className="header-notification__empty-icon" icon="bell" size="full-size" svg />
                    <div className="header-notification__empty-title">{t('notification.noNewNotifications')}</div>
                    <p className="header-notification__empty-description">{t('notification.currentlyNoNotifications')}</p>
                  </div>
                )}
              </section>
              {isInternalUser && (
                <footer className="header-notification__footer">
                  <Link
                    onClick={() => {
                      setShow(false);
                    }}
                    to={NOTIFICATIONS}
                  >
                    {t('notification.viewAllNotifications')}
                  </Link>
                </footer>
              )}
            </div>
          </div>
        )}
      </Overlay>
    </div>
  );
};

export default HeaderNotification;
